import { useRef, useState, useLayoutEffect, useEffect } from 'react';

const useSmartDropdown = ({ scrollContainerSelector = '.edit-meal-content' } = {}) => {
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const containerRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const [hasPosition, setHasPosition] = useState(false);
    const [isPartiallyHidden, setIsPartiallyHidden] = useState(false);

    const updateMenuPosition = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            minWidth: rect.width,
            zIndex: 9999
        });
        setHasPosition(true);
    };

    useLayoutEffect(() => {
        if (open) updateMenuPosition();
        else setHasPosition(false);
    }, [open]);

    useEffect(() => {
        containerRef.current = document.querySelector(scrollContainerSelector);
    }, [scrollContainerSelector]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let ticking = false;
        const handleScroll = () => {
            if (!open) return;
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    updateMenuPosition();
                    ticking = false;
                });
            }
        };
        const handleResize = () => open && updateMenuPosition();

        el.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);
        return () => {
            el.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [open]);

    useEffect(() => {
        if (!open || !triggerRef.current) return;
        const observer = new IntersectionObserver(([entry]) => !entry.isIntersecting && setOpen(false), {
            root: containerRef.current,
            threshold: 0.8
        });
        observer.observe(triggerRef.current);
        return () => observer.disconnect();
    }, [open]);

    useEffect(() => {
        if (!dropdownRef.current) return;
        const visibilityThresholds = Array.from({ length: 11 }, (_, i) => i / 10);
        const observer = new IntersectionObserver(
            ([entry]) => setIsPartiallyHidden(entry.intersectionRatio < 0.8),
            { root: containerRef.current, threshold: visibilityThresholds }
        );
        observer.observe(dropdownRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!open) return;
        const resizeObserver = new ResizeObserver(() => updateMenuPosition());
        resizeObserver.observe(containerRef.current || document.body);
        return () => resizeObserver.disconnect();
    }, [open]);

    return { triggerRef, dropdownRef, menuStyle, hasPosition, open, setOpen, isPartiallyHidden };
};

export default useSmartDropdown;
